/**
 * useMapUrlState.ts — T-237 hash URL-state controller for the router-less OptiYield SPA.
 *
 * WHAT IT DOES. The app has one view (MapShell) and no router (Ricardo's ruling), so shareable
 * and restore-on-reload state lives in the URL hash (#zoom/lat/lng&tract=…&pin=…, see urlHash.ts).
 * This hook owns the browser History for that state:
 *   - syncViewport()    → replaceState (throttled) as the map pans/zooms, so panning updates the
 *                         URL without polluting the Back stack.
 *   - commitSelection() → pushState on a discrete selection (tract pick / site score), so Back =
 *                         the previous selection and Forward redoes it.
 *   - on popstate       → restore the landed entry's viewport + tract via onRestore().
 *   - initial           → the boot hash parsed once, so MapShell can open on the deep-linked
 *                         camera and re-hydrate the tract (via T-233's fetchTractByGeoid).
 *
 * WHY IT SUBSUMES useBackNavigationGuard (T-071/T-081). That guard armed a history sentinel and
 * treated EVERY Back as an attempt to leave the SPA (warn via the in-app Leave dialog). That is
 * incompatible with Back-steps-through-selections: two popstate owners would fight, and the guard's
 * re-arm-on-every-Back truncates the forward history that Forward needs. So the two are unified
 * here. The sentinel still exists — it is the buffer that catches a Back which would exit the app —
 * but we now distinguish, by the landed entry's history.state marker, an in-app navigation from the
 * real exit:
 *   - state `{ oy: 'sentinel' }` or `{ oy: 'sel' }`  → one of OUR entries → restore, no dialog.
 *   - null / foreign state → the pre-sentinel app entry (the exit boundary) → re-arm + dialog.
 * With no selections this reduces exactly to the old guard (the first Back lands on the null app
 * entry → dialog), which is why T-071's F5 (re-arm on rapid double-Back) and F6 (fresh-tab explicit
 * nav) behaviours — and their tests — carry over unchanged.
 *
 * Pass `active = !!user` so the hook is inert on the login screen (never touch history before login).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { encodeMapHash, parseMapHash, type DecodedMapHash } from '../lib/urlHash'

/** What Back/Forward restore hands MapShell so it can move the map + set the selection. */
export interface RestoredMapState {
  zoom: number
  lat: number
  lng: number
  tract: string | null
  pin: { lat: number; lng: number } | null
}

interface UseMapUrlStateOptions {
  /** Signed in — mirrors the old guard's `active`; false on the login screen. */
  active: boolean
  /** Called on an in-app Back/Forward with the entry we landed on. */
  onRestore: (state: RestoredMapState) => void
}

export interface UseMapUrlState {
  /** Boot hash parsed once (null if none) — for the initial camera + tract re-hydration. */
  initial: DecodedMapHash | null
  /** Report the settled camera; writes the URL via throttled replaceState (no history entry). */
  syncViewport: (v: { center: [number, number]; zoom: number }) => void
  /** Record a discrete selection as a pushState entry (Back returns to the prior one). */
  commitSelection: (sel: { tract: string | null; pin: { lat: number; lng: number } | null }) => void
  /**
   * T-500: the absolute, shareable authed deep-link for the CURRENT viewport + selection, evaluated
   * at call time. It is `origin + pathname + <the exact hash this hook would write>`, so the link a
   * user copies is byte-for-byte what a reload/Back restores — location only, never a score.
   */
  getShareUrl: () => string
  showLeaveDialog: boolean
  /** Dismiss the Leave dialog and keep guarding. */
  stay: () => void
  /** Dismiss the Leave dialog and navigate out of the app for real. */
  leave: () => void
}

type EntryKind = 'sentinel' | 'sel'
const THROTTLE_MS = 250
// CONUS default (mirrors MapView) so an encode before the first real viewport is never NaN.
const DEFAULT_VIEWPORT = { zoom: 4, lat: 39.8, lng: -98.6 }

export function useMapUrlState({ active, onRestore }: UseMapUrlStateOptions): UseMapUrlState {
  const [showLeaveDialog, setShowLeaveDialog] = useState(false)

  // Parse the boot hash exactly once (window.location is stable for a session).
  const initial = useMemo(() => parseMapHash(window.location.hash), [])

  // Latest camera + selection, mirrored in refs so the throttled writer and pushState build the
  // current hash without re-binding. Seeded from the deep-link so the first write preserves it.
  const viewportRef = useRef(
    initial ? { zoom: initial.zoom, lat: initial.lat, lng: initial.lng } : { ...DEFAULT_VIEWPORT },
  )
  const selectionRef = useRef<{ tract: string | null; pin: { lat: number; lng: number } | null }>(
    initial ? { tract: initial.tract, pin: initial.pin } : { tract: null, pin: null },
  )
  // Kind of the CURRENT history entry, so a viewport replaceState re-stamps the right marker.
  const entryKindRef = useRef<EntryKind>('sentinel')
  const throttleRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Latest onRestore without re-binding the popstate listener.
  const onRestoreRef = useRef(onRestore)
  onRestoreRef.current = onRestore

  // Guard bookkeeping (ported from useBackNavigationGuard).
  const showingRef = useRef(false)
  showingRef.current = showLeaveDialog
  const leavingRef = useRef(false)
  const hadPriorEntryRef = useRef(false)

  const currentHash = useCallback(
    () =>
      encodeMapHash({
        ...viewportRef.current,
        tract: selectionRef.current.tract,
        pin: selectionRef.current.pin,
      }),
    [],
  )

  const armSentinel = useCallback(() => {
    // Push a sentinel entry — nothing visible changes, but the next Back pops this same-document
    // entry (firing popstate) instead of exiting the SPA. Keep the current URL (deep-link intact).
    entryKindRef.current = 'sentinel'
    window.history.pushState({ oy: 'sentinel' }, '', window.location.href)
  }, [])

  const syncViewport = useCallback(
    (v: { center: [number, number]; zoom: number }) => {
      viewportRef.current = { zoom: v.zoom, lat: v.center[1], lng: v.center[0] }
      if (throttleRef.current) clearTimeout(throttleRef.current)
      throttleRef.current = setTimeout(() => {
        throttleRef.current = null
        // replaceState — update the current entry's URL, never grow the Back stack (panning).
        window.history.replaceState({ oy: entryKindRef.current }, '', currentHash())
      }, THROTTLE_MS)
    },
    [currentHash],
  )

  const commitSelection = useCallback(
    (sel: { tract: string | null; pin: { lat: number; lng: number } | null }) => {
      selectionRef.current = { tract: sel.tract, pin: sel.pin }
      entryKindRef.current = 'sel'
      // pushState — a new Back-able entry for this selection (truncates any forward history).
      window.history.pushState({ oy: 'sel' }, '', currentHash())
    },
    [currentHash],
  )

  // T-500: the current location as an absolute, shareable link. viewportRef/selectionRef are
  // updated synchronously by syncViewport/commitSelection (only the replaceState write is
  // throttled), so this always reflects what is on screen at the moment the user clicks Share.
  const getShareUrl = useCallback(
    () => `${window.location.origin}${window.location.pathname}${currentHash()}`,
    [currentHash],
  )

  useEffect(() => {
    if (!active) return
    leavingRef.current = false
    // Capture BEFORE arming: on a fresh tab history.length === 1, so Leave has nowhere to go back
    // to and must navigate explicitly (T-081 F6).
    hadPriorEntryRef.current = window.history.length > 1
    armSentinel()

    const onPopState = (event: PopStateEvent) => {
      // leave() is intentionally navigating out — let it through without re-trapping.
      if (leavingRef.current) return

      const state = event.state as { oy?: EntryKind } | null
      if (state && (state.oy === 'sentinel' || state.oy === 'sel')) {
        // In-app Back/Forward: restore the entry we landed on from its URL. No dialog.
        entryKindRef.current = state.oy
        const decoded = parseMapHash(window.location.hash)
        const restored: RestoredMapState = decoded ?? {
          ...viewportRef.current,
          tract: null,
          pin: null,
        }
        viewportRef.current = { zoom: restored.zoom, lat: restored.lat, lng: restored.lng }
        selectionRef.current = { tract: restored.tract, pin: restored.pin }
        onRestoreRef.current(restored)
        return
      }

      // Exit boundary: landed on the pre-sentinel app entry (null/foreign state). Re-arm so a
      // rapid second Back stays trapped (F5), then open the Leave dialog once.
      armSentinel()
      if (showingRef.current) return
      setShowLeaveDialog(true)
    }
    window.addEventListener('popstate', onPopState)

    return () => {
      window.removeEventListener('popstate', onPopState)
      if (throttleRef.current) {
        clearTimeout(throttleRef.current)
        throttleRef.current = null
      }
      // Sentinel entry intentionally left in place — can't splice history, and leaving it gives the
      // desired "Back exits normally" behaviour after logout unmounts this hook.
    }
  }, [active, armSentinel])

  const stay = useCallback(() => {
    // popstate already re-armed the sentinel, so we're still guarded — just close.
    setShowLeaveDialog(false)
  }, [])

  const leave = useCallback(() => {
    setShowLeaveDialog(false)
    leavingRef.current = true
    if (hadPriorEntryRef.current) {
      // We sit on the re-armed sentinel with the app entry behind it and the page the user came
      // from behind that; skip both to actually leave the app.
      window.history.go(-2)
    } else {
      // Fresh tab: nothing precedes the app entry, so go() would be a no-op — navigate explicitly.
      window.location.assign(window.location.origin)
    }
  }, [])

  return { initial, syncViewport, commitSelection, getShareUrl, showLeaveDialog, stay, leave }
}
